import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import commissionService from '@/services/commissionService';
import type { CommissionConfig } from '@/types/commission';
import { Badge } from '@/components/ui/badge';
import { Settings, Save, User, Search, CheckCircle, XCircle, Euro, BarChart3 } from 'lucide-react';
import { cn } from '@/utils/cn';

interface Admin {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

interface AdminConfigData {
  admin: Admin;
  config: CommissionConfig | null;
  formData: {
    commission_rate: number;
    bonus_enabled: boolean;
    bonus_threshold: number;
    bonus_rate: number;
    notes: string;
  };
}

export default function CommissionConfigPage() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [adminConfigs, setAdminConfigs] = useState<AdminConfigData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState<AdminConfigData | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const navigationTabs = [
    { name: 'Dashboard', href: '/commissions/dashboard', icon: BarChart3, current: location.pathname === '/commissions/dashboard' },
    ...(isSuperAdmin ? [{ name: 'Configuration', href: '/commissions/config', icon: Settings, current: location.pathname === '/commissions/config' }] : [])
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger tous les admins
      const adminsResponse = await api.get('/users/admins');
      const allAdmins: Admin[] = adminsResponse.data;

      // Filtrer pour ne garder que les apporteurs d'affaires (exclure super_admin et admin)
      const admins = allAdmins.filter(admin => admin.role === 'apporteur');

      // Charger les configurations existantes
      const configs = await commissionService.listConfigs();

      // Créer la structure AdminConfigData
      const adminConfigsData: AdminConfigData[] = admins.map(admin => {
        const config = configs.find(c => c.admin_id === admin.id && c.is_active);
        return {
          admin,
          config: config || null,
          formData: {
            commission_rate: config?.commission_rate || 10, // 10% par défaut
            bonus_enabled: config?.bonus_enabled || false,
            bonus_threshold: config?.bonus_threshold || 50000,
            bonus_rate: config?.bonus_rate || 2,
            notes: config?.notes || ''
          }
        };
      });

      setAdminConfigs(adminConfigsData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (adminId: number) => {
    const adminConfig = adminConfigs.find(ac => ac.admin.id === adminId);
    if (!adminConfig) return;

    try {
      setSaving(adminId);
      setError(null);

      await commissionService.createConfig({
        admin_id: adminId,
        commission_rate: adminConfig.formData.commission_rate,
        start_date: new Date().toISOString().split('T')[0],
        end_date: undefined,
        notes: adminConfig.formData.notes,
        bonus_enabled: adminConfig.formData.bonus_enabled,
        bonus_threshold: adminConfig.formData.bonus_threshold,
        bonus_rate: adminConfig.formData.bonus_rate
      });

      await loadData();
    } catch (err: any) {
      console.error('Error saving config:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const updateFormData = (adminId: number, field: string, value: any) => {
    // Update adminConfigs
    setAdminConfigs(prev => prev.map(ac => {
      if (ac.admin.id === adminId) {
        return {
          ...ac,
          formData: {
            ...ac.formData,
            [field]: value
          }
        };
      }
      return ac;
    }));

    // Also update selectedAdmin if it's the same admin
    if (selectedAdmin && selectedAdmin.admin.id === adminId) {
      setSelectedAdmin(prev => prev ? {
        ...prev,
        formData: {
          ...prev.formData,
          [field]: value
        }
      } : null);
    }
  };

  const getAdminName = (admin: Admin): string => {
    if (admin.first_name && admin.last_name) {
      return `${admin.first_name} ${admin.last_name}`;
    }
    return admin.email;
  };

  // Filter admins based on search term
  const filteredAdmins = useMemo(() => {
    if (!searchTerm) return adminConfigs;

    const term = searchTerm.toLowerCase();
    return adminConfigs.filter(({ admin }) => {
      const name = getAdminName(admin).toLowerCase();
      const email = admin.email.toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [adminConfigs, searchTerm]);

  const handleSelectAdmin = (adminConfig: AdminConfigData) => {
    setSelectedAdmin(adminConfig);
  };

  const handleSaveAndClose = async (adminId: number) => {
    await handleSave(adminId);
    setSelectedAdmin(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuration des Commissions</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Gérez les taux de commission et bonus pour chaque apporteur d'affaires
        </p>
      </div>

      {/* Navigation Tabs */}
      {navigationTabs.length > 1 && (
        <div className="px-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {navigationTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.name}
                  to={tab.href}
                  className={cn(
                    'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm',
                    tab.current
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <Icon className="mr-2 h-5 w-5" />
                  {tab.name}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* Left Panel - Apporteur List */}
        <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un apporteur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Apporteur List */}
          <div className="flex-1 overflow-y-auto">
            {filteredAdmins.length === 0 ? (
              <div className="p-6 text-center">
                <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm ? 'Aucun apporteur trouvé' : 'Aucun apporteur dans le système'}
                </p>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {filteredAdmins.map(({ admin, config }) => (
                  <button
                    key={admin.id}
                    onClick={() => handleSelectAdmin({ admin, config, formData: adminConfigs.find(ac => ac.admin.id === admin.id)?.formData || { commission_rate: 10, bonus_enabled: false, bonus_threshold: 50000, bonus_rate: 2, notes: '' } })}
                    className={cn(
                      "w-full text-left p-3 rounded-lg border transition-all hover:shadow-sm",
                      selectedAdmin?.admin.id === admin.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {getAdminName(admin)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{admin.email}</p>
                        {config && (
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {config.commission_rate}%
                            </Badge>
                            {config.bonus_enabled && (
                              <Badge variant="secondary" className="text-xs">
                                ✨ Bonus
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end">
                        {config ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Configuration Form */}
        <div className="flex-1 flex flex-col">
          {!selectedAdmin ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Settings className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Sélectionnez un apporteur
                </h3>
                <p className="text-gray-600">
                  Choisissez un apporteur dans la liste pour configurer ses commissions
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              {/* Form Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {getAdminName(selectedAdmin.admin)}
                    </h2>
                    <p className="text-sm text-gray-500">{selectedAdmin.admin.email}</p>
                  </div>
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => {
                      setSelectedAdmin(null);
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={(e) => e.preventDefault()} className="space-y-6 max-w-2xl">
                  {/* Taux de base */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Taux de commission de base (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={selectedAdmin.formData.commission_rate}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        updateFormData(selectedAdmin.admin.id, 'commission_rate', value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Taux appliqué sur chaque vente (10% par défaut)
                    </p>
                  </div>

                  {/* Bonification */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        id="bonus-enabled"
                        checked={selectedAdmin.formData.bonus_enabled}
                        onChange={(e) => {
                          updateFormData(selectedAdmin.admin.id, 'bonus_enabled', e.target.checked);
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <label htmlFor="bonus-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        ✨ Activer la bonification
                      </label>
                    </div>

                    {selectedAdmin.formData.bonus_enabled && (
                      <div className="space-y-4 ml-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Euro className="inline h-4 w-4 mr-1" />
                              Seuil de CA mensuel (EUR)
                            </label>
                            <input
                              type="number"
                              step="1000"
                              min="0"
                              value={selectedAdmin.formData.bonus_threshold}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                updateFormData(selectedAdmin.admin.id, 'bonus_threshold', value);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Bonus supplémentaire (%)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={selectedAdmin.formData.bonus_rate}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                updateFormData(selectedAdmin.admin.id, 'bonus_rate', value);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            💡 <strong>Exemple:</strong> Si le CA mensuel dépasse {selectedAdmin.formData.bonus_threshold.toLocaleString('fr-FR')} €,
                            le taux passera de {selectedAdmin.formData.commission_rate}% à {selectedAdmin.formData.commission_rate + selectedAdmin.formData.bonus_rate}%
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notes (optionnel)
                    </label>
                    <textarea
                      value={selectedAdmin.formData.notes}
                      onChange={(e) => updateFormData(selectedAdmin.admin.id, 'notes', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="Informations complémentaires sur cette configuration..."
                    />
                  </div>
                </form>
              </div>

              {/* Form Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => {
                      setSelectedAdmin(null);
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => {
                      if (selectedAdmin) {
                        handleSaveAndClose(selectedAdmin.admin.id);
                      }
                    }}
                    disabled={saving === selectedAdmin?.admin.id}
                  >
                    <Save className="h-4 w-4" />
                    {saving === selectedAdmin?.admin.id ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
